import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import { getFarmerById, getFarmerKyc } from '@/store';
import ProfileTab from './components/ProfileTab';
import FieldTab from './components/FieldTab';
import SeasonTab from './components/SeasonTab';

type TabKey = 'profile' | 'fields' | 'seasons';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'profile', label: 'Hồ sơ', icon: 'ri-user-line' },
  { key: 'fields', label: 'Thửa đất', icon: 'ri-map-pin-line' },
  { key: 'seasons', label: 'Mùa vụ', icon: 'ri-calendar-line' },
];

export default function FarmerInfoPage() {
  useStore();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('profile');

  const farmerId = user?.farmerId;
  const farmer = getFarmerById(farmerId);
  const kyc = farmer ? getFarmerKyc(farmer) : null;

  if (!farmer) {
    return (
      <div className="px-4 py-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <span className="w-12 h-12 mx-auto flex items-center justify-center mb-3 text-slate-300">
            <i className="ri-user-unfollow-line text-3xl" />
          </span>
          <p className="text-slate-500 text-sm">Không tìm thấy thông tin nông hộ</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 max-w-[800px] mx-auto">
      {/* ========== HEADER ========== */}
      <div className="mx-4 mt-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-agrida-600 to-agrida-700 flex items-center justify-center shrink-0">
          <i className="ri-user-3-line text-white text-lg" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-gray-900 truncate">{farmer.name}</h2>
          <p className="text-[11px] text-gray-400 truncate">{[farmer.code, farmer.address].filter(Boolean).join(' · ')}</p>
        </div>
      </div>

      {/* ========== TABS ========== */}
      <div className="mx-4 mt-4 bg-white rounded-xl border border-slate-200 p-1 flex gap-1">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-colors min-h-[40px] flex-1 ${
                isActive ? 'bg-agrida-600 text-white' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center shrink-0"><i className={`${tab.icon} text-sm`} /></span>
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========== TAB CONTENT ========== */}
      <div className="mx-4 mt-3">
        {activeTab === 'profile' && <ProfileTab farmer={farmer} kyc={kyc || { kycStatus: 'registered' }} />}
        {activeTab === 'fields' && <FieldTab farmerId={farmer.id} />}
        {activeTab === 'seasons' && <SeasonTab farmerId={farmer.id} />}
      </div>
    </div>
  );
}